<script setup lang="ts">
import ChatRooms from '@/components/Sections/Communication/ChatRooms.vue'
import ChatRoom from '@/components/Sections/Communication/ChatRoom/ChatRoom.vue'
import ChatParticipants from '@/components/Sections/Communication/ChatRoom/ChatParticipants.vue'
import { onMounted, computed } from 'vue'
import { useChatStore } from '@/stores/chatStore.ts'
/**
 * Props
 */
const props = defineProps<{ roomId?: number }>()

/**
 * Stores
 */
const chatStore = useChatStore()

/**
 * Computed
 */
const sortedRooms = computed(() => {
  // Convert Map to array, filter out deleted rooms, and sort
  const roomsArray = Array.from(chatStore.rooms.values())
    .filter(room => room.deleted === 0)
  
  // Sort rooms with unread messages to the top, then by most recent message
  return roomsArray.sort((a, b) => {
    const roomStatusA = chatStore.roomReadStatus.get(a.id)
    const roomStatusB = chatStore.roomReadStatus.get(b.id)
    
    const hasUnreadA = roomStatusA?.unreadStatus || false
    const hasUnreadB = roomStatusB?.unreadStatus || false
    
    // If A has unread but B doesn't, A comes first
    if (hasUnreadA && !hasUnreadB) return -1
    // If B has unread but A doesn't, B comes first
    if (!hasUnreadA && hasUnreadB) return 1
    
    // Secondary sort: by last message received id (higher = more recent)
    const lastMessageIdA = roomStatusA?.lastMessageReceivedId || 0
    const lastMessageIdB = roomStatusB?.lastMessageReceivedId || 0
    
    // Sort in descending order (newest first)
    return lastMessageIdB - lastMessageIdA
  })
})

/**
 * Lifecycle
 */
onMounted(() => {
  if (props.roomId) {
    chatStore.setActiveRoomId(props.roomId)
  } else if (chatStore.activeRoomId === 0 && chatStore.rooms.size > 0) {
    // If no roomId is provided and no active room is set, set the first available room from sorted list as active
    const firstSortedRoom = sortedRooms.value[0]
    if (firstSortedRoom) {
      chatStore.setActiveRoomId(firstSortedRoom.id)
    }
  }
  // If no roomId is provided but activeRoomId is already set, keep using the existing activeRoomId
})
</script>

<template>
  <div class="flex w-full content-height-limit pt-1">
    <ChatRooms />
    <ChatRoom />
    <ChatParticipants />
  </div>
</template>

<style scoped lang="scss"></style>
